import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function HomePage() {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	
	const careerPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/career", payload);
			
			// Loop data (array)
			setJobs(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		careerPost();
	}, []);

  // Categorize jobs
	const academicJobs = jobs? jobs.filter((job) => job.job_type === "Academic")  : [];
	const nonAcademicJobs = jobs? jobs.filter((job) => job.job_type === "Non Academic")  : [];
	const generalStaffJobs = jobs? jobs.filter((job) => job.job_type === "General Staff")  : [];

	return (
    <div className="min-h-screen bg-gradient-to-b from-#B7EAFC via-[#F0FCFF] to-[#F0FCFF] overflow-hidden">
      {/* === Hero Section === */}
      <section className="relative text-center py-24 px-6 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-[#016C94] mb-3">
            Explore Available Job Opportunities
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover open positions across UCTC — Academic, Non-Academic, and General Staff roles.
          </p>
        </motion.div>

        {/* Hero SVG Wave */}
        <svg
			className="absolute bottom-0 left-0 w-[100vw] h-32 text-blue-200"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 1440 320"
			preserveAspectRatio="none"
		>
			<path
			  fill="currentColor"
			  fillOpacity="1"
			  d="M0,256L48,240C96,224,192,192,288,170.7C384,149,480,139,576,160C672,181,768,235,864,224C960,213,1056,139,1152,122.7C1248,107,1344,149,1392,170.7L1440,192L1440,320L0,320Z"
			/>
		</svg>
      </section>

      {/* === Academic Jobs Section === */}
      <CategorySection title="Academic Positions" color="#6BCCF2" jobs={academicJobs} />

      {/* === Non-Academic Jobs Section === */}
      <CategorySection title="Non-Academic Positions" color="emerald" jobs={nonAcademicJobs} />

      {/* === General Staff Section === */}
      <CategorySection title="General Staff Positions" color="amber" jobs={generalStaffJobs} />
    </div>
  );
}

// === Category Section Component ===
function CategorySection({ title, color, jobs }) {
  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-10"
      >
        <h2 className={`text-3xl font-bold text-${color}-700 mb-3`}>
          {title}
        </h2>

        {/* SVG Divider */}
        <svg
          className={`mx-auto w-40 h-3 text-${color}-500`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 10"
          fill="currentColor"
        >
          <path d="M0,5 Q25,0 50,5 T100,5" />
        </svg>
      </motion.div>

      {/* Cards */}
      {jobs.length > 0 ? (
        <motion.div
          className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.15 },
            },
          }}
        >
          {jobs.map((job) => (
            <motion.div
              key={job.sl}
              className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-blue-100"
              whileHover={{ scale: 1.03 }}
            >
              <h3 className="text-xl font-semibold text-[#016C94] mb-2">
                {job.designation_name}
              </h3>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Department:</strong> {job.department_name}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Gender:</strong> {job.gender}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Deadline:</strong>{" "}
                <span className="text-red-500 font-medium">{job.deadline}</span>
              </p>
              <p className="text-sm text-gray-600 mb-3">
                <strong>Open Date:</strong> {job.open_date}
              </p>

              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-500">
                  Grade: {job.grade_name}
                </span>
                <Link
                  to={`/career/${job.sl}`}
                  className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm hover:bg-[#016C94] transition-all duration-300"
                >
                  View Details
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <p className="text-center text-gray-500 mt-6">
          No {title.toLowerCase()} available at the moment.
        </p>
      )}
    </section>
  );
}
